export type StorefrontConfigIdentity = {
  version: 1
  integrationServer: string
  lojaId: number
  publicUrl: string
}

export type ExpectedStorefrontConfigIdentity = StorefrontConfigIdentity

export type StorefrontRuntime = {
  storefront_identity: {
    version: 1
    integration_server: string
    loja_id: number
    public_url: string
  }
  deployment: {
    build_id: string
  }
}

declare global {
  interface Window {
    __STOREFRONT_RUNTIME__?: unknown
  }
}

const normalizeText = (value: unknown) => String(value ?? '').trim()

export const normalizeStorefrontIntegrationServer = (value: unknown) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/^c2s_/, '')

const normalizePositiveInteger = (value: unknown) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0 || !Number.isInteger(parsed)) return 0
  return parsed
}

export const normalizeStorefrontPublicOrigin = (value: unknown) => {
  const raw = normalizeText(value)
  if (!raw) return ''

  try {
    const url = new URL(raw)
    if (
      !['http:', 'https:'].includes(url.protocol)
      || url.username
      || url.password
      || url.pathname !== '/'
      || url.search
      || url.hash
    ) {
      return ''
    }

    return url.origin.toLowerCase()
  } catch {
    return ''
  }
}

const readRuntimeSource = () => {
  if (typeof window !== 'undefined') return window.__STOREFRONT_RUNTIME__
  return (globalThis as typeof globalThis & { __STOREFRONT_RUNTIME__?: unknown }).__STOREFRONT_RUNTIME__
}

export const readStorefrontRuntime = (): StorefrontRuntime | null => {
  const source = readRuntimeSource()
  if (!source || typeof source !== 'object' || Array.isArray(source)) return null

  const record = source as Record<string, unknown>
  const rawIdentity = record.storefront_identity
  const deployment = record.deployment
  if (
    !rawIdentity || typeof rawIdentity !== 'object' || Array.isArray(rawIdentity)
    || !deployment || typeof deployment !== 'object' || Array.isArray(deployment)
  ) {
    return null
  }

  const identity = rawIdentity as Record<string, unknown>
  const deploymentRecord = deployment as Record<string, unknown>
  const version = normalizePositiveInteger(identity.version)
  const integrationServer = normalizeStorefrontIntegrationServer(identity.integration_server)
  const lojaId = normalizePositiveInteger(identity.loja_id)
  const publicUrl = normalizeStorefrontPublicOrigin(identity.public_url)
  const buildId = normalizeText(deploymentRecord.build_id)

  if (version !== 1 || !integrationServer || !lojaId || !publicUrl || !buildId) return null

  return {
    storefront_identity: {
      version: 1,
      integration_server: integrationServer,
      loja_id: lojaId,
      public_url: publicUrl,
    },
    deployment: { build_id: buildId },
  }
}

export const resolveExpectedStorefrontConfigIdentity = (): ExpectedStorefrontConfigIdentity => {
  const runtime = readStorefrontRuntime()
  if (!runtime) throw new Error('storefront_runtime_missing')

  const expected: ExpectedStorefrontConfigIdentity = {
    version: 1,
    integrationServer: runtime.storefront_identity.integration_server,
    lojaId: runtime.storefront_identity.loja_id,
    publicUrl: runtime.storefront_identity.public_url,
  }

  if (typeof window !== 'undefined' && normalizeStorefrontPublicOrigin(window.location.origin) !== expected.publicUrl) {
    throw new Error('storefront_runtime_origin_mismatch')
  }

  return expected
}

export const readStorefrontConfigIdentity = (
  payload: unknown
): StorefrontConfigIdentity | null => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null

  const payloadRecord = payload as Record<string, unknown>
  const rawIdentity = payloadRecord.storefront_identity ?? payloadRecord.storefrontIdentity
  if (!rawIdentity || typeof rawIdentity !== 'object' || Array.isArray(rawIdentity)) return null

  const identityRecord = rawIdentity as Record<string, unknown>
  const version = normalizePositiveInteger(identityRecord.version)
  const integrationServer = normalizeStorefrontIntegrationServer(
    identityRecord.integration_server ?? identityRecord.integrationServer
  )
  const lojaId = normalizePositiveInteger(
    identityRecord.loja_id ?? identityRecord.lojaId
  )
  const publicUrl = normalizeStorefrontPublicOrigin(
    identityRecord.public_url ?? identityRecord.publicUrl
  )

  if (version !== 1 || !integrationServer || !lojaId || !publicUrl) return null

  return { version: 1, integrationServer, lojaId, publicUrl }
}

export const assertStorefrontConfigIdentity = (
  payload: unknown,
  expected: ExpectedStorefrontConfigIdentity = resolveExpectedStorefrontConfigIdentity()
) => {
  const identity = readStorefrontConfigIdentity(payload)
  if (!identity) throw new Error('site_config_identity_missing')

  if (
    identity.integrationServer !== expected.integrationServer
    || identity.lojaId !== expected.lojaId
    || identity.publicUrl !== expected.publicUrl
  ) {
    throw new Error('site_config_identity_mismatch')
  }

  return identity
}

export const buildStorefrontConfigCacheScope = (input: {
  expected: ExpectedStorefrontConfigIdentity
  origin?: unknown
}) => [
  `v${input.expected.version}`,
  input.expected.integrationServer,
  input.expected.lojaId,
  encodeURIComponent(input.expected.publicUrl),
].join('|')
